import * as React from 'react';
import axios from "axios";
import { IResponse } from "../../types/api";
import { ISong, ISongStats } from "../song/types";
import { ActionIcon, Anchor, Button, createStyles, Flex, NumberInput, Select, Text, TextInput, Checkbox } from "@mantine/core";
import Page from "../../components/layout/Page";
import { produce } from "immer";
import {
    IconChevronDown,
    IconChevronLeft,
    IconChevronRight,
    IconChevronsLeft,
    IconChevronsRight,
    IconCircleCheck,
    IconPencil,
    IconPlus,
    IconTableImport,
    IconTableExport,
    IconTrash
} from "@tabler/icons-react";
import { slice, sort, reverse } from "ramda";
import { useCategoryNumber } from "../../hooks";
import { vocalItems, CLIENT_ROUTE } from "../song/constants";
import SongStats from "../song/SongStats";
import { Link } from "react-router-dom";
import { writeFileXLSX, utils } from "xlsx";
import dayjs from "dayjs";
import ScrollControl from "../../components/common/ScrollControl";
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import useStore from '../../store';

const abc = 'абвгдеёжзийклмноөпрстуүфхцчшщъьыэюяabcdefghijklmnopqrstuvwxyz1234567890 -()';

const translate = (word: string) => {

    let value = '';
    for (const char of word) {
        value += abc.indexOf(char.toLowerCase());
    }

    return value;
    // return Number(value);
}

const useStyles = createStyles((theme) => {
    const headStyle = {
        border: `1px solid ${theme.colors.gray[5]}`,
        padding: `.3rem ${theme.spacing.xs}px`,
        fontSize: 14
    }
    return {
        table: {
            borderCollapse: 'separate',
            borderSpacing: 0,
            '> thead': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                overflow: 'hidden',
                background: theme.white
            }
        },
        tbody: {
            '> tr:nth-of-type(odd)': {
                backgroundColor: theme.colors.gray[1]
            }
        },
        head: headStyle,
        sortableHead: {
            ...headStyle,
            ':hover': {
                cursor: 'pointer'
            }
        },
        cell: {
            border: `1px solid ${theme.colors.gray[5]}`,
            padding: `.1rem ${theme.spacing.xs}px`,
            fontSize: 14
        },
        columnFilter: {
            border: 0,
            outline: 'none',
            width: 200
        },
        paginationWrapper: {
            justifyContent: 'space-between',
            display: 'flex',
            alignItems: 'center',
            marginTop: theme.spacing.xs
        },
        paginationLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.xs
        },
        pageSize: {
            width: 80
        },
        pagination: {
            display: 'flex',
            gap: theme.spacing.xs - 5,
            alignItems: 'center'
        },
        quickJumper: {
            width: 80
        },
    }
})

interface ISort {
    order: 'asc' | 'desc' | null;
    key: keyof ISong | null;
}

interface IState {
    records: ISong[];
    pageSize: number;
    page: number;
    filters: Record<string, string | null>;
    sort: ISort;
    selectedRows: string[];
    songStats: ISongStats;
}

interface IColumn {
    label: string;
    key: keyof ISong;
}

type SetListResponse = {
    type: 'SET_RECORDS',
    payload: {
        records: ISong[];
        songStats: ISongStats;
    };
};

type SetPageSize = {
    type: 'SET_PAGE_SIZE',
    payload: {
        pageSize: number;
    };
};

type SetPage = {
    type: 'SET_PAGE',
    payload: {
        page: number;
    };
};

type SetFilterValue = {
    type: 'SET_FILTER_VALUE',
    payload: {
        key: string;
        value: string | null;
    };
};

type SetSort = {
    type: 'SET_SORT',
    payload: {
        key: keyof ISong;
    };
};

type HandleRowSelection = {
    type: 'HANDLE_ROW_SELECTION',
    payload: {
        rowId: string;
    };
};

type HandleAllRowSelect = {
    type: 'HANDLE_ALL_ROW_SELECT',
    payload: {

    }
};



export type Action =
    SetListResponse
    | SetPageSize
    | SetPage
    | SetFilterValue
    | SetSort
    | HandleRowSelection
    | HandleAllRowSelect
    ;


const initialState: IState = {
    records: [],
    pageSize: 50,
    page: 1,
    filters: {},
    sort: {
        key: null,
        order: null,
    },
    selectedRows: [],
    songStats: {
        code1Count: 0,
        generalCount: 0,
        lyricCount: 0,
        totalCount: 0,
        vivaCount: 0,
        canPlayInDuul: 0,
        image: 0,
        hasMLyrics: 0,
        licenseNext: 0,
        licenseMOSCAP: 0,
        licenseMCPP: 0,
        hasInstrumentFile: 0,
        hasOriginalFile: 0,
        hasLyrics: 0
    }
}

const reducer = produce(
    (draft: IState, action: Action) => {
        switch(action.type) {
            case 'SET_RECORDS': {
                const { records, songStats } = action.payload;

                records.sort((a, b) => {
                    if (a.featuredOrder !== b.featuredOrder) {
                        return a.featuredOrder - b.featuredOrder;
                    }
                    if (!a.groupNumber || ['700', '555', '560', '600'].includes(a.groupNumber.toString())) {
                        return 1;
                    }
                    if (!b.groupNumber || ['700', '555', '560', '600'].includes(b.groupNumber.toString())) {
                        return -1;
                    }

                    if (a.groupNumber !== b.groupNumber) {
                        return -1 * (a.groupNumber - b.groupNumber);
                    }
                    return a.title.localeCompare(b.title);
                });

                draft.records = records;
                draft.songStats = songStats;
                break;
            }
            case 'SET_PAGE_SIZE': {
                const { pageSize } = action.payload;
                draft.pageSize = pageSize;
                break;
            }
            case 'SET_PAGE': {
                const { page } = action.payload;
                draft.page = page;
                break;
            }
            case 'SET_FILTER_VALUE': {
                const { key, value } = action.payload;
                draft.filters[key] = value;
                break;
            }
            case 'SET_SORT': {
                const { key } = action.payload;
                if (key === draft.sort.key) {
                    if (draft.sort.order === 'asc') {
                        draft.sort.order = 'desc';
                        break;
                    }
                    if (draft.sort.order === 'desc') {
                        draft.sort.order = null;
                        draft.sort.key = null;
                        break;
                    }
                    break;
                } else {
                    draft.sort.order = 'asc';
                    draft.sort.key = key;
                    break;
                }
            }
            case 'HANDLE_ROW_SELECTION': {
                const { rowId } = action.payload;
                const index = draft.selectedRows.findIndex(id => id === rowId);
                if (index > -1) {
                    draft.selectedRows.splice(index, 1);
                } else {
                    draft.selectedRows.push(rowId);
                }
                break;
            }
            case 'HANDLE_ALL_ROW_SELECT': {

                break;
            }
            default:
                break;
        }
    }
);

interface IData {
    filteredCount: number;
    tableRows: ISong[];
    pageCount: number;
    filteredRows: ISong[];
}

const Library = () => {

    const { classes } = useStyles();
    const roleName = useStore(state => state.auth.roleName) ?? '';

    const categories = useCategoryNumber();

    const [ state, dispatch ] = React.useReducer(reducer, initialState);

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.post<IResponse<ISong[]>>('/api/song/library-no-filter');

            let generalCount = 0;
            let code1Count = 0;
            let vivaCount = 0;
            let lyricCount = 0;
            let totalCount = (data.result ?? []).length;
            let canPlayInDuul = 0;

            let hasMLyrics = 0;
            let image = 0;
            let licenseNext = 0;
            let licenseMCPP = 0;
            let licenseMOSCAP = 0;

            let hasLyrics = 0;
            let hasInstrumentFile = 0;
            let hasOriginalFile = 0;

            const general = (data.result ?? []).filter(record => {
                if (
                    record?.licenseLyric?.toLowerCase?.()?.includes?.('next') ||
                    record?.licenseSound?.toLowerCase?.()?.includes?.('next') ||
                    record?.licenseSinger?.toLowerCase?.()?.includes?.('next') ||
                    record?.licenseVideo?.toLowerCase?.()?.includes?.('next')
                ) {
                    licenseNext++;
                }
                if (
                    record?.licenseLyric?.toUpperCase()?.includes?.('MOSCAP') ||
                    record?.licenseSound?.toUpperCase?.()?.includes?.('MOSCAP') ||
                    record?.licenseSinger?.toUpperCase?.()?.includes?.('MOSCAP') ||
                    record?.licenseVideo?.toUpperCase?.()?.includes?.('MOSCAP')
                ) {
                    licenseMOSCAP++;
                }
                if (
                    record?.licenseLyric?.toUpperCase?.()?.includes?.('MCPP') ||
                    record?.licenseSound?.toUpperCase?.()?.includes?.('MCPP') ||
                    record?.licenseSinger?.toUpperCase?.()?.includes?.('MCPP') ||
                    record?.licenseVideo?.toUpperCase?.()?.includes?.('MCPP')
                ) {
                    licenseMCPP++;
                }
                record.hasMLyrics && hasMLyrics++;
                record.hasLyrics && hasLyrics++;
                record.hasInstrumentFile && hasInstrumentFile++;
                record.hasOriginalFile && hasOriginalFile++;
                record.image && image++;
                record.canPlayInDuul && canPlayInDuul++;
                return [6, 7, 8].includes(record.categoryNumber);
            })
            generalCount = general.length;

            for (const record of general) {
                if (record.code1) {
                    code1Count++;
                }
                if (record.vivaNumber && record.vivaNumber !== 560) {
                    vivaCount++;
                }
                if (record.hasLyrics) {
                    lyricCount++;
                }
            }

            const songStats: ISongStats = {
                code1Count,
                generalCount,
                lyricCount,
                totalCount,
                vivaCount,
                canPlayInDuul,
                image,
                hasMLyrics,
                licenseNext,
                licenseMCPP,
                licenseMOSCAP,
                hasLyrics,
                hasInstrumentFile,
                hasOriginalFile
            }

            dispatch({
                type: 'SET_RECORDS',
                payload: {
                    records: data.result,
                    songStats
                }
            })
        }
        void fetchData();
    }, []);

    const gotoPage = (page: number) => {
        dispatch({
            type: 'SET_PAGE',
            payload: {
                page
            }
        })
    }

    const columns: IColumn[] = [
        {
            key: 'featuredOrder',
            label: '⭐',
        },
        {
            key: 'groupNumber',
            label: 'Бүлэг',
        },
        {
            key: 'vivaNumber',
            label: 'Вива',
        },
        {
            key: 'title',
            label: 'Дууны нэр',
        },
        {
            key: 'singer',
            label: 'Дуучин',
        },
        {
            key: 'image',
            label: 'Зураг',
        },
        {
            key: 'code1',
            label: 'Код-1',
        },
        {
            key: 'code2',
            label: 'Код-2',
        },
        {
            key: 'categoryNumber',
            label: 'Ангилал',
        },
        {
            key: 'lyricBy',
            label: 'Үг',
        },
        {
            key: 'musicBy',
            label: 'Ая',
        },
        {
            key: 'youtubeLink',
            label: 'YouTube',
        },
        {
            key: 'phonograms',
            label: 'Фонограм',
        },
        {
            key: 'isClip',
            label: 'Клип эсэх',
        },
        {
            key: 'contract',
            label: 'Гэрээ',
        },
        {
            key: 'access',
            label: 'Дуулалт',
        },
        {
            key: 'licenseLyric',
            label: 'Зөвшөөрөл үг',
        },
        {
            key: 'licenseSound',
            label: 'Зөвшөөрөл ая',
        },
        {
            key: 'licenseVideo',
            label: 'Зөвшөөрөл бичлэг',
        },
        {
            key: 'licenseSinger',
            label: 'Зөвшөөрөл дуучин',
        },
        {
            key: 'likeCount',
            label: 'Like',
        },
        {
            key: 'vocal',
            label: 'Vocal',
        },
        {
            key: 'hasMLyrics',
            label: 'Босоо бичиг /Файл/',
        },
        {
            key: 'hasLyrics',
            label: 'Үг /Файл/',
        },
        {
            key: 'hasInstrumentFile',
            label: 'Дууны /Файл/',
        },
        {
            key: 'hasOriginalFile',
            label: 'Дуучинтай /Файл/',
        },
        {
            key: 'hasLyricText',
            label: 'Үг /Lyrics/',
        },
        {
            key: 'isActive',
            label: 'Төлөв',
        },
        {
            key: 'createdAt',
            label: 'Үүссэн',
        },
    ];

    const getSortedRecords = (array: ISong[]): ISong[] => {
        const { key, order } = state.sort;

        if (key && order) {
            const sorted = sort((a, b) => {
                const valueA = a[key];
                const valueB = b[key];
                if (key === 'featuredOrder') {
                    if (!a.isFeatured) {
                        return 1;
                    }
                    if (!b.isFeatured) {
                        return -1;
                    }
                }
                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return valueA - valueB;
                }
                if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
                    if (valueA && valueB) {
                        return 0;
                    }
                    if (!valueA) {
                        return 1;
                    }
                    return -1;
                }
                if (typeof valueA !== 'string') {
                    return 1;
                }
                if (typeof valueB !== 'string') {
                    return -1;
                }
                return valueA.localeCompare(valueB);
            }, array);

            if (!order) {
                return sorted;
            }

            return order === 'asc' ? sorted : reverse(sorted);
        }

        const sorted = sort((a, b) => {

            for (const filterKey in state.filters) {
                const fKey = filterKey as keyof ISong;
                const filterValue = state?.filters?.[fKey]?.toLowerCase?.().trim() ?? null;
                if (!filterValue) {
                    continue;
                }
                const valA = a[fKey];
                const valB = b[fKey];
                const lowerValueA = typeof valA === 'string' ? valA.toLowerCase().trim() : null;
                const lowerValueB = typeof valB === 'string' ? valB.toLowerCase().trim() : null;

                const aEquals = typeof lowerValueA === 'string' && filterValue && lowerValueA === filterValue;
                const bEquals = typeof lowerValueB === 'string' && filterValue && lowerValueB === filterValue;

                if (aEquals && bEquals) {
                    // @ts-ignore
                    return lowerValueA?.localeCompare(lowerValueB) ?? 0;
                }
                if (aEquals) {
                    return -1;
                }
                if (bEquals) {
                    return 1;
                }

                const aStartsWith = typeof lowerValueA === 'string' && filterValue && lowerValueA.startsWith(filterValue);
                const bStartsWith = typeof lowerValueB === 'string' && filterValue && lowerValueB.startsWith(filterValue);

                if (aStartsWith && bStartsWith) {
                    // @ts-ignore
                    return lowerValueA?.localeCompare(lowerValueB) ?? 0;
                }

                if (aStartsWith) {
                    return -1;
                }

                if (bStartsWith) {
                    return 1;
                }

                const aIncludes = typeof lowerValueA === 'string' && filterValue && (lowerValueA.includes(filterValue) && !lowerValueA.endsWith(filterValue));
                const bIncludes = typeof lowerValueB === 'string' && filterValue && (lowerValueB.includes(filterValue) && !lowerValueB.endsWith(filterValue));

                if (aIncludes && bIncludes) {
                    // @ts-ignore
                    return lowerValueA?.localeCompare(lowerValueB) ?? 0;
                }

                if (aIncludes) {
                    return -1
                }

                if (bIncludes) {
                    return 1;
                }

                const aEndsWith = typeof lowerValueA === 'string' && filterValue && lowerValueA.endsWith(filterValue);
                const bEndsWith = typeof lowerValueB === 'string' && filterValue && lowerValueB.endsWith(filterValue);

                if (aEndsWith && bEndsWith) {
                    // @ts-ignore
                    return lowerValueA?.localeCompare(lowerValueB) ?? 0;
                }

                if (aEndsWith) {
                    return -1;
                }

                if (bEndsWith) {
                    return 1;
                }

                // if (typeof lowerValueA === 'string' && filterValue && lowerValueA === filterValue) {
                //     return -1;
                // }
                //
                // if (typeof lowerValueB === 'string' && filterValue && lowerValueB === filterValue) {
                //     return 1;
                // }

                // if (typeof lowerValueA === 'string' && filterValue && lowerValueA.startsWith(filterValue)) {
                //     return -1;
                // }
                //
                // if (typeof lowerValueB === 'string' && filterValue && lowerValueB.startsWith(filterValue)) {
                //     return 1;
                // }

                // if (typeof lowerValueA === 'string' && filterValue && (lowerValueA.includes(filterValue) && !lowerValueA.endsWith(filterValue))) {
                //     return -1;
                // }
                //
                // if (typeof lowerValueB === 'string' && filterValue && (lowerValueB.includes(filterValue) && !lowerValueB.endsWith(filterValue))) {
                //     return 1;
                // }

                // if (typeof lowerValueA === 'string' && filterValue && lowerValueA.endsWith(filterValue)) {
                //     return -1;
                // }
                //
                // if (typeof lowerValueB === 'string' && filterValue && lowerValueB.endsWith(filterValue)) {
                //     return 1;
                // }
            }
            if (!order || !key) {
                return 0;
            }
            const valueA = a[key];
            const valueB = b[key];

            if (!valueA && !valueB) {
                return 0;
            }
            if (typeof valueA !== 'string') {
                return 1;
            }
            if (typeof valueB !== 'string') {
                return -1;
            }
            return valueA.localeCompare(valueB)
            // console.log('translateA>>>>>', translate(valueA.toString()));
            // console.log('translateB>>>>>', translate(valueB.toString()));
            return translate(valueA.toString()).localeCompare(translate(valueB.toString()));
            // return translate(valueA.toString()) - translate(valueB.toString());
        }, array);

        if (!order) {
            return sorted;
        }

        return order === 'asc' ? sorted : reverse(sorted);

        // return array.sort((a, b) => {
        //     const valueA = a[key];
        //     const valueB = b[key];
        //     if (!valueA && !valueB) {
        //         return 0;
        //     }
        //     if (!valueA) {
        //         return 1;
        //     }
        //     if (!valueB) {
        //         return -1;
        //     }
        //     return translate(valueA.toString()).localeCompare(valueB.toString());
        // })

        // return array;
        // return array.sort((a, b) => {
        //     return 1;
        // })
    }

    const getFilteredRecords = (): ISong[] => {
        const filtered: ISong[] = [];
        for (const record of state.records) {
            let include = true;
            for (const filterKey in state.filters) {
                const key = filterKey as keyof ISong;
                const filterValue = state.filters[key];
                const value = record?.[key]?.toString?.()?.toLowerCase?.() ?? null;
                if (filterValue) {
                    switch (key) {
                        case 'categoryNumber':
                        case 'vocal':
                        case 'isClip':
                        case 'hasLyrics':
                        case 'hasMLyrics':
                        case 'isActive':
                        case 'groupNumber':
                        case 'title':
                        case 'singer':
                        case 'code1':
                        case 'code2':
                        case 'phonograms':
                        case 'contract':
                        case 'licenseLyric':
                        case 'licenseSound':
                        case 'licenseVideo':
                        case 'hasInstrumentFile':
                        case 'hasLyricText':
                        case 'hasOriginalFile':
                        case 'licenseSinger': {
                            if (!value?.includes?.(filterValue.toLowerCase())) {
                                include = false;
                                break;
                            }
                            break;
                        }
                        case 'lyricBy':
                        case 'image': {
                            if ((filterValue === 'yes' && !value) || (filterValue === 'no' && value)) {
                                include = false;
                                break;
                            }
                            break;
                        }
                    }
                }
            }
            include && filtered.push(record);
        }
        return filtered;
    }

    const { filteredCount, tableRows, pageCount, filteredRows } = React.useMemo(() => {
        const filtered = getFilteredRecords();
        const sorted = getSortedRecords(filtered);
        const startIndex = (state.page - 1) * state.pageSize;
        const endIndex = startIndex + state.pageSize;
        const data: IData = {
            filteredCount: filtered.length,
            tableRows: slice(startIndex, endIndex, sorted),
            pageCount: Math.ceil(filtered.length / state.pageSize),
            filteredRows: filtered
        }
        return data;
    }, [state.page, state.pageSize, state.records, state.filters, state.sort]);

    const canPreviousPage = React.useMemo(() => state.page > 1, [state.page]);
    const canNextPage = React.useMemo(() => state.page < pageCount, [pageCount, state.page]);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const getColumnFilterItem = (columnKey: keyof ISong): React.ReactNode => {
        switch (columnKey) {
            case 'groupNumber':
            case 'title':
            case 'singer':
            case 'code1':
            case 'code2': {
                return (
                    <TextInput
                        autoComplete={'off'}
                        className={classes.columnFilter}
                        style={{ width: columnKey === 'groupNumber' ? 50 : 100 }}
                        value={state.filters[columnKey] ?? ''}
                        onChange={e => {
                            const { value } = e.target;
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    value,
                                    key: columnKey
                                }
                            })
                            // setFilter(/**/e.target.value || undefined); // Set undefined to remove the filter entirely
                        }}
                        placeholder={'Хайх...'}
                    />
                )
            }
            case 'categoryNumber': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={categories}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'image': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            { value: 'no', label: 'Зураггүй' },
                            { value: 'yes', label: 'Зурагтай' }
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'isClip': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            { value: 'false', label: 'Үгүй' },
                            { value: 'true', label: 'Тийм' }
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'hasInstrumentFile':
            case 'hasOriginalFile':
            case 'hasLyricText':
            case 'hasMLyrics': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            { value: 'false', label: 'Үгүй' },
                            { value: 'true', label: 'Тийм' }
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'phonograms': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            'ОРГИНАЛ',
                            'ХИЙСЭН',
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'contract': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            'ХЭЛЦЭЛ',
                            'УРАМШУУЛАЛ',
                            'ХЭЛЦЭЛ / УРАМШУУЛАЛ',
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'vocal': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={vocalItems}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'isActive': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            { value: 'false', label: 'Идэвхгүй' },
                            { value: 'true', label: 'Идэвхтэй' }
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'licenseLyric':
            case 'licenseSound':
            case 'licenseVideo':
            case 'licenseSinger': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            'NEXT GALAXY',
                            'MOSCAP',
                            'MCPP',
                            'НДХ',
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'lyricBy': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            { value: 'yes', label: 'Үгтэй' },
                            { value: 'no', label: 'Үггүй' }
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            case 'hasLyrics': {
                return (
                    <Select
                        placeholder="Сонгох"
                        data={[
                            { value: 'true', label: 'Үгтэй' },
                            { value: 'false', label: 'Үггүй' }
                        ]}
                        onChange={(value) => {
                            dispatch({
                                type: 'SET_FILTER_VALUE',
                                payload: {
                                    key: columnKey,
                                    value,
                                }
                            })
                        }}
                        value={state.filters[columnKey] ?? null}
                        style={{ width: 100 }}
                        clearable
                        dropdownPosition='bottom'
                    />
                )
            }
            default: {
                return null;
            }
        }
    }

    const handleSort = (key: keyof ISong) => {
        dispatch({
            type: 'SET_SORT',
            payload: {
                key
            }
        })
    }

    const renderCell = (column: IColumn, row: ISong): React.ReactNode => {
        const textValue = row[column.key] as string;
        switch (column.key) {
            case 'isClip':
            case 'hasMLyrics':
            case 'hasLyrics':
            case 'hasInstrumentFile':
            case 'hasLyricText':
            case 'hasOriginalFile':
            case 'isActive': {
                const color = textValue ? 'green' : 'red';
                return (
                    <div style={{ textAlign: 'center' }}>
                        <IconCircleCheck
                            size={20}
                            color={color}
                        />
                    </div>
                )
            }
            case 'categoryNumber': {
                const category = categories.find(c => c.value === textValue?.toString());
                return category?.label ?? textValue;
            }
            case 'vocal': {
                const vocal = vocalItems.find(v => v.value === textValue?.toString());
                return vocal?.label ?? textValue;
            }
            case 'image': {
                if (!textValue) {
                    return null;
                }
                return (
                    <Anchor href={textValue} target='_blank'>
                        Харах
                    </Anchor>
                )
            }
            case 'youtubeLink': {
                if (!textValue) {
                    return null;
                }
                return (
                    <Anchor href={textValue} target='_blank'>
                        Нээх
                    </Anchor>
                )
            }
            case 'title': {
                return (
                    <Anchor href={`/song/edit?${row.id}`} target='_blank'>
                        {textValue}
                    </Anchor>
                )
            }
            case 'featuredOrder': {
                if (!row.isFeatured) {
                    return null;
                }
                return `${textValue}⭐`;
            }
            case 'createdAt': {
                if (!row.createdAt) {
                    return null;
                }
                return (
                    <div style={{ whiteSpace: 'nowrap' }}>
                        {dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                )
            }
            default: {
                return (
                    textValue
                )
            }
        }
    }

    const handleRowSelect = (id: string) => {
        dispatch({
            type: 'HANDLE_ROW_SELECTION',
            payload: {
                rowId: id,
            }
        })
    };

    const downloadExcel = () => {

        const array = [];

        // @ts-ignore
        for (const [ index, record ] of filteredRows.entries()) {
            array.push({
                '№': index + 1,
                'ОНЦОЛСОН': record.featuredOrder,
                'a100_id': record.groupNumber,
                'viva_id': record.vivaNumber,
                'ДУУНЫ НЭР': record.title,
                'ДУУЧИД, хамтлаг': record.singer,
                'A-100': record.code1,
                'HD-VIVA': record.code2,
                'ЖАНР': record.categoryNumber,
                'ҮГ': record.lyricBy,
                'АЯ': record.musicBy,
                'YOUTUBE ЛИНКНҮҮД': record.youtubeLink,
                'Фонограм': record.phonograms,
                'КЛИП': record.isClip ? 'КЛИП' : null,
                'ГЭРЭЭ': record.contract,
                'Дуулалт': record.access,
                'Зөвшөөрөл үг': record.licenseLyric,
                'Зөвшөөрөл ая': record.licenseSound,
                'Зөвшөөрөл бичлэг': record.licenseVideo,
                'Зөвшөөрөл дуучин': record.licenseSinger,
                'LIKE': record.likeCount,
                'VOC': record.vocal === 'none' ? null : record.vocal,
                'ТӨЛӨВ': record.isActive ? 'Идэвхтэй' : 'Идэвхгүй',
                'ҮҮССЭН': record.createdAt,
                'Зураг': record.image ?? null,
                // 'Онцолсон': record.featuredOrder,
                // 'Бүлэг': record.groupNumber,
                // 'Вива': record.vivaNumber,
                // 'Дууны нэр': record.title,
                // 'Дууны нэр /Англи/': record.titleEn,
                // 'Дуучин': record.singer,
                // 'Дуучин /Англи/': record.singerEn,
                // 'Код-1': record.code1,
                // 'Код-2': record.code2,
                // 'Ангилал': record.categoryNumber,
                // 'Үг': record.lyricBy,
                // 'Ая': record.musicBy,
                // 'YouTube': record.youtubeLink,
                // 'Фонограм': record.phonograms,
                // 'Клип эсэх': record.isClip,
                // 'Гэрээ': record.contract,
                // 'Хандалт': record.access,
                // 'Зөвшөөрөл үг': record.licenseLyric,
                // 'Зөвшөөрөл ая': record.licenseSound,
                // 'Зөвшөөрөл бичлэг': record.licenseVideo,
                // 'Зөвшөөрөл дуучин': record.licenseSinger,
                // 'Like': record.likeCount,
                // 'Vocal': record.vocal,
                // 'Төлөв': record.isActive,
                // 'Үүссэн': record.createdAt,
            })
        }

        const ws = utils.json_to_sheet(array);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Дуунууд");
        writeFileXLSX(wb, `Дуунууд-${dayjs().format('YYYY/MM/DD-HH:mm')}.xlsx`);
    }

    const deleteRecord = (id: string) => {
        openConfirmModal({
            title: 'Устгах',
            children: 'Мэдээлэл устгах уу?',
            labels: {
                confirm: 'Устгах',
                cancel: 'Цуцлах'
            },
            async onConfirm() {
                const { data } = await axios.delete(`/api/song/${id}`);
                if (data.statusCode === 200) {
                    showNotification({
                        title: 'Мэдэгдэл',
                        message: 'Амжилттай',
                        color: 'green'
                    })
                    window.location.reload();
                } else {
                    showNotification({
                        title: 'Мэдэгдэл',
                        message: 'Алдаа гарлаа',
                        color: 'red'
                    })
                }
            }
        });
    }

    return (
        <Page>
            <Page.Header title={'Дууны сан'}>
                <Text fw={600} fz={20} mr='md' c='gray.8'>
                    Нийт: &nbsp;
                    {filteredCount}
                </Text>
                <Button
                    component={Link}
                    to={'/song/import-excel'}
                    mr={'sm'}
                    leftIcon={<IconTableImport size={20}/>}
                >
                    Excel оруулах
                </Button>
                <Button
                    rightIcon={<IconTableExport size={20}/>}
                    onClick={downloadExcel}
                    mr='sm'
                >
                    Excel татах
                </Button>
                <Button
                    component={Link}
                    to={`${CLIENT_ROUTE}/new`}
                    leftIcon={<IconPlus size={18}/>}
                    color={'color-secondary'}
                >
                    Үүсгэх
                </Button>
            </Page.Header>
            <Page.Body style={{ overflow: 'hidden' }}>
                <SongStats
                    controlled={true}
                    data={state.songStats}
                />
                <div style={{ overflow: 'auto' }} ref={containerRef}>
                    <table className={classes.table}>
                        <thead>
                        <tr>
                            <th className={classes.head}>№</th>
                            {
                                columns.map((column) => {
                                    const isSorted = state.sort.key === column.key;
                                    const sortOrder = state.sort.order;
                                    return (
                                        <th
                                            key={column.key}
                                            className={classes.sortableHead}
                                            onClick={() => handleSort(column.key)}
                                        >
                                            {column.label}
                                            {
                                                isSorted
                                                    ?   sortOrder === 'asc' ? '🔼' : '🔽'
                                                    :   null
                                            }
                                        </th>
                                    )
                                })
                            }
                            {/*ACTIONS COLUMN*/}
                            <th className={classes.head}/>
                        </tr>
                        <tr>
                            <th className={classes.head}/>
                            {
                                columns.map((column) => {
                                    return (
                                        <th className={classes.head} key={`filter-${column.key}`}>
                                            {getColumnFilterItem(column.key)}
                                        </th>
                                    )
                                })
                            }
                            <th className={classes.head}/>
                        </tr>
                        </thead>
                        <tbody className={classes.tbody}>
                        {
                            tableRows.map((row, index) => {
                                return (
                                    <tr key={row.id}>
                                        {/*<td>*/}
                                        {/*    <Checkbox*/}
                                        {/*        onChange={() => handleRowSelect(row._id)}*/}
                                        {/*        checked={state.selectedRows.includes(row._id)}*/}
                                        {/*    />*/}
                                        {/*</td>*/}
                                        <td className={classes.cell}>{((state.page - 1) * state.pageSize) + index + 1}</td>
                                        {
                                            columns.map((column) => {
                                                return (
                                                    <td key={`${column.key}-${index + 1}`} className={classes.cell}>
                                                        {/*@ts-ignore*/}
                                                        {renderCell(column, row)}
                                                    </td>
                                                )
                                            })
                                        }
                                        <td className={classes.cell}>
                                            <Flex
                                                justify={'flex-end'}
                                                align={'center'}
                                            >
                                                <ActionIcon
                                                    color={'blue'}
                                                    onClick={() => {
                                                        window.open(`/song/edit?${row.id}`, '_blank')
                                                    }}
                                                >
                                                    <IconPencil size={18}/>
                                                </ActionIcon>
                                                {
                                                    roleName?.toLowerCase() === 'super' && (
                                                        <ActionIcon color={'red'} onClick={() => deleteRecord(row.id)}>
                                                            <IconTrash size={18}/>
                                                        </ActionIcon>
                                                    ) 
                                                }
                                            </Flex>
                                        </td>
                                    </tr>
                                )
                            })
                        }
                        </tbody>
                    </table>
                </div>
            </Page.Body>
            <Page.Footer>
                <div className={classes.paginationWrapper}>
                    <div className={classes.paginationLeft}>
                        <Select
                            className={classes.pageSize}
                            rightSection={<IconChevronDown size={14} />}
                            rightSectionWidth={30}
                            styles={{ rightSection: { pointerEvents: 'none' } }}
                            data={['30', '50', '100']}
                            size={'sm'}
                            value={state.pageSize.toString()}
                            onChange={(value) => {
                                if (value) {
                                    dispatch({
                                        type: 'SET_PAGE_SIZE',
                                        payload: {
                                            pageSize: Number(value)
                                        }
                                    })
                                }
                            }}
                        />
                        <ScrollControl
                            vertical={containerRef}
                            horizontal={containerRef}
                        />
                    </div>
                    <div className={classes.pagination}>
                        <span>
                            Хуудас{' '}
                            <strong>
                                {state.page} / {pageCount}
                            </strong>{' '}
                        </span>

                        <NumberInput
                            type="number"
                            value={state.page}
                            min={2}
                            onChange={e => {
                                // TODO: check if value is able to jump to ?
                                const page = e ?? 0;
                                // const page = e.target.value ? Number(e.target.value) - 1 : 0
                                gotoPage(page)
                            }}
                            className={classes.quickJumper}
                            size='xs'
                        />

                        <ActionIcon
                            onClick={() => {
                                gotoPage(1)
                            }}
                            disabled={!canPreviousPage}
                            variant={'filled'}
                            color={'color-primary.6'}
                        >
                            <IconChevronsLeft size={15}/>
                        </ActionIcon>
                        <ActionIcon
                            onClick={() => gotoPage(state.page - 1)}
                            disabled={!canPreviousPage}
                            variant={'filled'}
                            color={'color-primary.6'}
                        >
                            <IconChevronLeft size={15}/>
                        </ActionIcon>
                        <ActionIcon
                            onClick={() => gotoPage(state.page + 1)}
                            disabled={!canNextPage}
                            variant={'filled'}
                            color={'color-primary.6'}
                        >
                            <IconChevronRight size={15}/>
                        </ActionIcon>
                        <ActionIcon
                            onClick={() => gotoPage(pageCount)}
                            disabled={!canNextPage}
                            variant={'filled'}
                            color={'color-primary.6'}
                        >
                            <IconChevronsRight size={15}/>
                        </ActionIcon>
                    </div>
                </div>
            </Page.Footer>
        </Page>
    )
}

export default Library;